<template>
  <div v-if="!!currentTourDayItem" class="h-100">
    <v-loader v-if="status === 'loading' && suppliersStatus === 'loading'" />

    <form v-else id="tour-day-item-resource-select" class="d-flex flex-column justify-content-between h-100">
      <div>
        <fieldset>
          <legend class="visually-hidden">{{ $t("tourBuilder.searchTourDayItemResource") }}</legend>

          <div class="form-outline">
            <div class="d-flex justify-content-between">
              <label class="label">
                <span
                  v-if="
                    (currentTourDayItem.caseDayItem.type !== undefined &&
                      currentTourDayItem.caseDayItem.type === 'stage_information') ||
                    currentTourDayItem.caseDayItem.type === 'general_information'
                  "
                >
                  {{ $t("general.shared.selected") }}
                </span>
                <span v-else>
                  {{ $t("general.shared.searchByNameOrLocation") }}
                </span>
              </label>
            </div>
            <v-select
              v-if="
                (currentTourDayItem.caseDayItem.type !== undefined &&
                  currentTourDayItem.caseDayItem.type === 'stage_information') ||
                currentTourDayItem.caseDayItem.type === 'general_information'
              "
              id="tour-day-item-supplier-select"
              v-model="supplier"
              :filterable="false"
              :options="stageResource"
              class="select-search"
              label="name"
              @search="onSearch"
              @option:selected="loadResourceSelected"
            >
              <template v-slot:no-options>
                {{ $t("tourBuilder.writeSearchResources") }}
              </template>

              <template v-slot:option="option">
                <div class="d-center">
                  <span>{{ option?.title }}</span>
                </div>
              </template>

              <template v-slot:selected-option="option">
                <div class="selected d-center py-8">
                  <span>{{ option?.title }}</span>
                </div>
              </template>

              <template #list-footer>
                <li ref="load" class="create-client">
                  <v-button
                    class="btn-icon h-fit fw-normal shadow-none py-0 px-16"
                    size="sm"
                    variant="link"
                    @click="openOffCanvas('the-resource-create-offcanvas')"
                  >
                    <v-icon icon="plus" size="xs" space="me-8" />
                    {{ $t("clientTour.createResource") }}
                  </v-button>
                </li>
              </template>
            </v-select>
            <v-select
              v-else
              id="tour-day-item-supplier-select"
              v-model="supplier"
              :filterable="false"
              :options="suppliers"
              class="select-search"
              label="name"
              @search="onSearch"
              @option:selected="loadSupplierSelected"
            >
              <template v-slot:no-options>
                {{ $t("tourBuilder.writeSearchSuppliers") }}
              </template>

              <template v-slot:option="option">
                <div class="d-center">
                  <span class="text-sm fw-light ms-4">
                    ({{ $str.roundedToFixed($str.milesToKilometers(Number(option?.distance)), 1) }}
                    {{ $t("tourBuilder.KilometersShorted") }})
                  </span>

                  <span>{{ option?.name }}</span>

                  <span v-if="!!option?.locationName" class="text-sm fw-light ms-4">
                    ({{ option?.locationName }})
                  </span>
                </div>
              </template>

              <template v-slot:selected-option="option">
                <div class="selected d-center py-8">
                  <span>{{ option?.name }}</span>

                  <span v-if="!!option?.distance" class="text-sm fw-light ms-4">
                    ({{ $str.roundedToFixed($str.milesToKilometers(Number(option.distance)), 1) }}
                    {{ $t("tourBuilder.KilometersShorted") }})
                  </span>

                  <span v-if="!!option?.locationName" class="text-sm fw-light ms-4">
                    ({{ option?.locationName }})
                  </span>
                </div>
              </template>

              <template #list-footer>
                <li ref="load" class="create-client">
                  <v-button
                    class="btn-icon h-fit fw-normal shadow-none py-0 px-16"
                    size="sm"
                    variant="link"
                    @click="openOffCanvas('the-supplier-create-offcanvas')"
                  >
                    <v-icon icon="plus" size="xs" space="me-8" />
                    {{ $t("clientTour.createSupplier") }}
                  </v-button>
                </li>
              </template>
            </v-select>
          </div>
          <div
            v-if="!!supplier && !!supplier.id && stageResource?.length === 0"
            class="resources-container form-outline"
          >
            <div class="d-flex justify-content-between">
              <label class="label">
                <span>{{ $t("general.shared.products") }}</span>
              </label>

              <label class="label me-20 bg-light">
                <v-button
                  class="btn-rates p-0 mx-4"
                  variant="icon"
                  @click="openOffCanvas('the-supplier-details-rates-off-canvas')"
                >
                  <v-icon class="px-0" icon="euro" />
                </v-button>

                <v-button
                  class="btn-rates p-0 mx-4"
                  variant="icon"
                  @click="openOffCanvas('the-supplier-details-offcanvas')"
                >
                  <v-icon class="px-0" icon="pencil" />
                </v-button>

                <v-button class="btn-rates p-0 mx-4" variant="icon" @click="loadSupplierSelected">
                  <v-icon class="px-0" icon="refresh" />
                </v-button>
              </label>
            </div>

            <div v-if="!!resources && resources.length > 0" class="form-control">
              <div
                v-for="resource in resources"
                :key="resource.id"
                class="resource-item d-flex justify-content-between align-items-center py-4"
              >
                <v-checkbox
                  :id="`tour-day-item-create-resource-${resource.id}`"
                  :for-key="`tour-day-item-create-resource-${resource.id}`"
                  :label="resource.name"
                  class="form-check-inline me-32"
                  label-class="ms-0"
                  @update:modelValue="setResource($event, resource)"
                />

                <div v-if="!!checkExistResource(resource)" class="d-flex">
                  <v-button
                    v-if="checkExistResource(resource).quantity > 1"
                    class="p-0"
                    size="sm"
                    variant="icon"
                    @click="removeResource(resource)"
                  >
                    <v-icon icon="remove" size="sm" />
                  </v-button>

                  <div class="mx-8">{{ checkExistResource(resource).quantity }}</div>

                  <v-button class="p-0" size="sm" variant="icon" @click="addResource(resource)">
                    <v-icon icon="circle-plus" size="sm" />
                  </v-button>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
        <fieldset>
          <div class="search-separator-container mb-24 pb-24">
            <div class="separator-text-container mb-16" />
          </div>

          <legend class="visually-hidden">{{ $t("tourBuilder.tourDayItemResourceDates") }}</legend>

          <div>
            <template v-for="(resource, index) in resourcesSelected" :key="index">
              <div :id="`accordionSupplierResource${resource.data.id}`" class="accordion border mb-16">
                <div class="supplier-resource-details accordion-item mb-0">
                  <div :id="`headingSupplierResource${resource.data.id}`" class="accordion-header">
                    <button
                      :aria-controls="`collapseSupplierResource${resource.data.id}`"
                      :data-bs-target="`#collapseSupplierResource${resource.data.id}`"
                      aria-expanded="false"
                      class="accordion-button collapsed p-8"
                      data-bs-toggle="collapse"
                      type="button"
                    >
                      <span class="d-flex flex-column">
                        <span class="fw-medium pe-80">
                          {{ resource.data.name }}
                        </span>

                        <span class="text-xs ms-4">
                          {{ $t("general.shared.pax") }}: {{ resourceItemTotalResourcePax(resource.data.id) }}
                          -
                          {{ $t("general.shared.quantityShort") }}: {{ resource.tourDayItemResources.length }}
                        </span>
                      </span>
                    </button>
                  </div>

                  <div
                    :id="`collapseSupplierResource${resource.data.id}`"
                    :aria-labelledby="`headingSupplierResource${resource.data.id}`"
                    :data-bs-parent="`#accordionSupplierResource${resource.data.id}`"
                    class="accordion-collapse collapse"
                  >
                    <div class="accordion-body p-8">
                      <div class="d-flex justify-content-between align-items-center pe-8">
                        <v-checkbox
                          v-model="groupByMode[resource.data.id]"
                          label="Group By"
                          class="text-nowrap mt-8"
                          @click.prevent="toggleGroupByMode(resource.data.id)"
                        />
                      </div>
                      <template v-if="groupByMode[resource.data.id]">
                        <div
                          v-for="(tourDayItemResource, indexItemResources) in resourceItemGroupResource(
                            resource.data.id
                          )"
                          :key="`tour-day-item-resource-${indexItemResources}`"
                          class="border rounded-xs p-12 gx-8 mb-8"
                        >
                          <hr v-if="indexItemResources > 0" class="item-form-separator my-8" />
                          <div class="d-flex justify-content-between align-items-center">
                            <div class="fw-medium text-sm">
                              {{ resource.data.name }}
                            </div>
                            <!-- TODO: Dissucs about id -->
                            <!-- <v-button
                                  size="sm"
                                  variant="icon"
                                  @click="removeTourDayItemResource(tourDayItemResource.id, true)"
                                >
                                  <v-icon icon="trash" size="sm" />
                                </v-button> -->
                          </div>

                          <the-tour-day-item-details-resource-form
                            v-if="!!tourDayItemResource"
                            :data="tourDayItemResource"
                            :days-count="daysCount"
                            :index="indexItemResources"
                            :init-date="currentTourDayItem?.day_date"
                            :type="resource.data.type"
                            @refresh-resource-data="setItemResourceData($event, index, indexItemResources)"
                            version="new"
                          />
                        </div>
                      </template>
                      <template v-else>
                        <div
                          v-for="(tourDayItemResource, indexItemResources) in resource.tourDayItemResources"
                          :key="indexItemResources"
                          class="border rounded-xs p-12 gx-8 mb-8"
                        >
                          <hr v-if="indexItemResources > 0" class="item-form-separator my-8" />
                          <div class="d-flex justify-content-between align-items-center">
                            <div class="fw-medium text-sm">
                              {{ resource.data.name }}
                              <span v-if="resource.tourDayItemResources.length > 1" class="fw-normal text-xs">
                                (#{{ indexItemResources + 1 }})
                              </span>
                            </div>

                            <v-button
                              size="sm"
                              variant="icon"
                              @click="removeTourDayItemResource(resource.data.id, tourDayItemResource)"
                            >
                              <v-icon icon="trash" size="sm" />
                            </v-button>
                          </div>

                          <the-tour-day-item-details-resource-form
                            v-if="!!tourDayItemResource"
                            :data="tourDayItemResource"
                            :days-count="daysCount"
                            :index="indexItemResources"
                            :init-date="currentTourDayItem?.day_date"
                            :type="resource.data.type"
                            @refresh-resource-data="setItemResourceData($event, index, indexItemResources)"
                            version="new"
                          />
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </fieldset>
        <fieldset v-if="!!selectedResouce && !!supplier && !!supplier.id">
          <div class="selected d-center py-8">
            <div class="d-flex justify-content-center">
              <label class="label">
                <span>{{ $t("general.shared.basicInformation") }}</span>
              </label>
            </div>
            <div class="text-sm d-flex py-4">
              <span class="me-16 fw-medium">{{ $t("general.shared.title") }}: </span>
              <span>{{ selectedResouce?.title }}</span>
            </div>
            <div class="text-sm d-flex py-4">
              <span class="me-16 fw-medium">{{ $t("general.shared.location") }}: </span>
              <span>
                {{ selectedResouce?.location?.name }}
                <v-icon v-if="!!selectedResouce?.destination" icon="arrow-right" size="xs" space="px-8" />
                {{ selectedResouce?.destination }}
              </span>
            </div>
            <div class="text-sm d-flex py-4">
              <span class="me-16 fw-medium">{{ $t("general.shared.types") }}: </span>
              <span>{{ selectedResouce?.destination }}</span>
            </div>
            <div class="text-sm d-flex py-4">
              <span class="me-16 fw-medium">{{ $t("general.shared.description") }}: </span>
              <span v-html="selectedResouce?.description" />
            </div>
            <div class="search-separator-container mb-8">
              <div class="separator-text-container">
                <span class="separator-text text-sm">{{ $t("general.shared.links") }}</span>
              </div>
            </div>
            <div v-if="selectedResouce?.links" class="text-sm d-flex flex-column py-4">
              <span class="me-16 fw-medium">Links: </span>
              <template v-for="url in JSON.parse(selectedResouce?.links)" :key="url">
                <a :href="url" class="resource-link btn-link" rel="noopener noreferrer" target="_blank">
                  {{ url }}
                </a>
              </template>
            </div>
          </div>
        </fieldset>
      </div>
      <v-button
        v-if="
          currentTourDayItem.caseDayItem.type === 'stage_information' ||
          currentTourDayItem.caseDayItem.type === 'general_information'
        "
        :disabled="!selectedResouce || stageResource?.length === 0"
        class="btn-submit-form btn-icon w-fit align-self-end mt-8"
        size="sm"
        variant="primary"
        @click="createCaseDayItemResource"
      >
        {{ $t("general.button.create") }}
        <v-icon icon="arrow-right" size="sm" space="ms-12" />
      </v-button>
      <v-button
        v-else
        :disabled="!resourcesSelected || resourcesSelected.length === 0"
        class="btn-submit-form btn-icon w-fit align-self-end mt-8"
        size="sm"
        variant="primary"
        @click="createCaseDayItemResource"
      >
        {{ $t("general.button.create") }}
        <v-icon icon="arrow-right" size="sm" space="ms-12" />
      </v-button>
    </form>

    <the-supplier-details-rates-off-canvas
      v-if="offCanvasToShow === 'the-supplier-details-rates-off-canvas'"
      :supplier-id="supplier.id"
      @closed="closeOffCanvas"
    />

    <the-supplier-create-offcanvas
      v-if="offCanvasToShow === 'the-supplier-create-offcanvas'"
      @closed="
        closeOffCanvas();
        debounceFunction();
      "
      @reload-suppliers="closeOffCanvas"
      @open-details="supplier = $event"
    />

    <the-supplier-details-offcanvas
      v-if="offCanvasToShow === 'the-supplier-details-offcanvas'"
      :is-archived="false"
      :is-double-off-canvas="false"
      :supplier-data="supplier"
      @closed="
        closeOffCanvas();
        loadSupplierSelected();
      "
    />

    <the-resource-create-offcanvas
      v-if="offCanvasToShow === 'the-resource-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-resources="closeOffCanvas"
    />

    <the-case-day-item-pax-selection
      v-if="showPaxSelection"
      :selected-resource="selectedResource"
      :case-day-item-resources="caseDayItemResources"
      :supplier-resource-id="supplierResourcesId"
      :agency="agency"
      :case-id="currentTourDayItem.caseDayItem.case_day.case_id"
      :case-day-id="currentTourDayItem.caseDayItem.case_day_id"
      :item-id="itemId"
      @closed="showPaxSelection = false"
      @refresh="refresh"
    />
  </div>
</template>

<script lang="ts" setup name="TheCaseDayItemDetailsCreateForm">
import { computed } from "vue";
import vSelect from "vue-select";
import { VCheckbox } from "@uniqoders/form";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import Supplier from "@/api/objects/Supplier";
import Resource from "@/api/objects/Resource";
import { useUserStore } from "@/stores/user";
import api from "@/api";
import SupplierResource from "@/api/objects/SupplierResource";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import TheTourDayItemDetailsResourceForm from "@/components/tour/TheTourDayItemDetailsResourceForm.vue";
import TheSupplierDetailsRatesOffCanvas from "@/components/supplier/parts/TheSupplierDetailsRatesOffCanvas.vue";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import TheSupplierCreateOffcanvas from "@/components/supplier/TheSupplierCreateOffcanvas.vue";
import TheSupplierDetailsOffcanvas from "@/components/supplier/TheSupplierDetailsOffcanvas.vue";
import TheResourceCreateOffcanvas from "@/components/resource/TheResourceCreateOffcanvas.vue";
import TheCaseDayItemPaxSelection from "@/components/case/TheCaseDayItemPaxSelection.vue";
import { ref } from "vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  clientTourId: {
    type: Number,
    required: true,
  },
  tourId: {
    type: Number,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
  version: {
    type: String,
    default: "",
  },
  daysCount: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(["closed", "emitReloadTour"]);

const userStore = useUserStore();
const currentTourDayItem = computed(() => userStore.currentTourDayItem);
const { openOffCanvas, closeOffCanvas, offCanvasToShow } = useOffCanvasUtils();

const resourceKey = ref<number>(0);
// const totalPax = ref<number>(0);
const suppliers = ref<Supplier[]>([]);
const stageResource = ref<Resource[]>([]);
const supplier = ref<Partial<Supplier>>({});
const resources = ref<SupplierResource[]>([]);
const resourcesMapped = ref<SupplierResource[]>([]);
const suppliersStatus = ref<string>("loading");
const resourceStatus = ref<string>("loading");
// const mix = ref<string>("");

const resourcesSelected = ref<Record<string, any>[]>([]);
const resourcesNextStep = ref<boolean>(false);
const showPaxSelection = ref<boolean>(false);
const selectedResource = ref<any>({});
const itemId = ref<number>(0);
const caseDayItemResources = ref<any[]>([]);
const supplierResourcesId = ref<number>(0);

// const filters = reactive<Record<string, any>>({
//   mix: "",
//   type: currentTourDayItem.value?.type || "",
// });

// const timers = reactive<Record<string, any>>({
//   mix: null,
// });

const media = ref<any>(null);
const selectedResouce = ref<any>(null);
const groupByMode = ref<{ [key: number]: boolean }>({});
const createData = ref<any[]>([]);
const newItemID = ref<number[]>([]);

const toast = useToast();
const { t } = useI18n();

const setItemResourceData = (value: any, resourceIndex: number, indexItemResources: number) => {
  const resource = resourcesSelected.value[resourceIndex]?.tourDayItemResources[indexItemResources];
  if (!resource) return;

  resource.pax = value.pax;
  resource.endDate = value.endDate;
  resource.nights = value.nights;
  resource.observations = value.observations;
  resource.client_observations = value.client_observations;
  resource.nReserve = value.nReserve;
  resource.origin = value.origin;
  resource.destination = value.destination;
  resource.initHour = value.initHour;
  resource.endHour = value.endHour;
  resource.isOptional = value.isOptional;

  media.value = value.media;
};

const onSearch = (search: string, loading: (state: boolean) => void) => {
  if (!search.length) return;

  loading(true);

  const isInformationType =
    (currentTourDayItem.value?.caseDayItem?.type &&
      ["stage_information", "general_information"].includes(currentTourDayItem.value?.caseDayItem?.type)) ||
    false;

  if (isInformationType) {
    loadResources(loading, search);
  } else {
    loadSuppliers(loading, search);
  }
};

const loadSuppliers = async (loading: (state: boolean) => void, search: string) => {
  if (!currentTourDayItem.value) return;  

  loading(true);
  suppliersStatus.value = "loading";

  try {
    const query = {
      filters: {
        type: currentTourDayItem.value?.caseDayItem?.type,
        mix: search,
      },
      location_id: currentTourDayItem.value.is_stage
        ? currentTourDayItem.value.destination_id
        : currentTourDayItem.value.origin_id,
    };

    suppliers.value = await api.supplier.all(query);
  } catch (e: any) {
    console.error(e.response?.data || e);
  } finally {
    suppliersStatus.value = "loaded";
    loading(false);
  }
};

const loadResources = async (loading: (state: boolean) => void, search: string) => {
  if (!currentTourDayItem.value) return;

  loading(true);

  try {
    const query = {
      filters: {
        type: currentTourDayItem.value.caseDayItem?.type,
        title: search,
      },
    };

    stageResource.value = await api.resource.all(query);
  } catch (e: any) {
    console.error(e.response?.data || e);
  } finally {
    suppliersStatus.value = "loaded";
    loading(false);
  }
};

const openDetailsOffCanvas = (offCanvasName: string, selectedSupplier: any) => {
  supplier.value = selectedSupplier;

  if (offCanvasToShow.value !== offCanvasName) {
    openOffCanvas(offCanvasName);
  }
};

const debounceFunction = () => {
  setTimeout(() => {
    loadSupplierSelected();
  }, 600);
};

// ✅ Load Supplier Selected
const loadSupplierSelected = async () => {
  if (!currentTourDayItem.value || !supplier.value) return;

  try {
    resourceStatus.value = "loading";
    const supplierData = await api.supplier.retrieve(supplier.value.id);

    resources.value = (supplierData.resources || []).filter(
      (resource: SupplierResource) => !resource.isHidden && !(resource as any).is_hidden,
    );
  } catch (e: any) {
    console.error(e.response?.data || e);
    toast.error(e.response?.data?.message || "Error loading supplier");
  } finally {
    resourceStatus.value = "loaded";
  }
};

// ✅ Load Resource Selected
const loadResourceSelected = async () => {
  if (!currentTourDayItem.value || !supplier.value) return;

  try {
    resourceStatus.value = "loading";
    const resourceData = await api.resource.retrieve(supplier.value.id);

    selectedResouce.value = resourceData;
  } catch (e: any) {
    console.error(e.response?.data || e);
    toast.error(e.response?.data?.message || "Error loading resource");
  } finally {
    resourceStatus.value = "loaded";
  }
};

const toggleGroupByMode = (resourceId: number) => {
  if (groupByMode[resourceId] === undefined) {
    groupByMode[resourceId] = true;
  } else {
    groupByMode[resourceId] = !groupByMode[resourceId];
  }
};

// Group resource items
const resourceItemGroupResource = (resourceId: number) => {
  const resource = resourcesSelected.value.find((r) => r.data.id === resourceId);

  const grouped = resource?.tourDayItemResources?.reduce((acc: any, cur: any) => {
    const resId = cur.resource.id;
    if (!acc[resId]) {
      acc[resId] = { ...cur, id: [cur.id], pax: cur.pax };
    } else {
      acc[resId].id.push(cur.id);
      if (cur.resource.type !== "transport") {
        acc[resId].pax += cur.pax;
      } else {
        acc[resId].pax = cur.pax;
      }
    }
    return acc;
  }, {});

  createData.value = Object.values(grouped || {});
  return Object.values(grouped || {});
};

const createCaseDayItemResource = async () => {
  if (!currentTourDayItem.value) return;

  try {
    resourceStatus.value = "loading";

    // Build per-resource payload with pax and nights from the locally edited resources.
    const resourcesPayload = resourcesSelected.value.flatMap((rs) =>
      rs.tourDayItemResources.map((tir: any) => ({
        id: rs.data.id,
        pax: tir.pax ?? 1,
        nights: tir.nights ?? 1,
      }))
    );

    await api.case.caseDayItemUpdate(
      props.agency,
      props.clientTourId,
      currentTourDayItem.value.caseDayItem.case_day_id,
      currentTourDayItem.value.caseDayItem.id,
      {
        type:
          currentTourDayItem.value.caseDayItem.type === "stage_information" ||
          currentTourDayItem.value.caseDayItem.type === "general_information"
            ? "resource"
            : "supplier",
        model_id: supplier.value.id,
        resourceIds: newItemID.value,
        resources: resourcesPayload,
      }
    );

    emit("emitReloadTour");
    await userStore.setCurrentCaseDayItem({
      agency: props.agency,
      caseId: props.clientTourId,
      caseDayId: currentTourDayItem.value.caseDayItem.case_day_id,
      caseDayItemId: currentTourDayItem.value.caseDayItem.id,
    });
    newItemID.value = [];

    resourceStatus.value = "loaded";
  } catch (e: any) {
    resourceStatus.value = "loaded";
    console.error(e.response?.data || e);
  }
};

const setResource = (checkBoxValue: boolean, resource: any) => {
  if (checkBoxValue) addResource(resource);
  else removeResource(resource, true);
};

const checkExistResource = (resource: any) => resourcesSelected.value.find((r) => r.data.id === resource.id);

// Add a resource
const addResource = (resource: any) => {
  if (!currentTourDayItem.value) return;

  const resourceExist = checkExistResource(resource);

  newItemID.value.push(resource.id);
  
  let pax: number;
  const dayPaxCount = currentTourDayItem.value?.paxes?.length ?? 1;
  let qtyToAdd = 1;

  switch (resource.type) {
    case "hotel":
      if (resource?.name?.includes("Single")) pax = 1;
      else if (resource?.name?.includes("Double")) pax = 2;
      else if (resource?.name?.includes("Triple")) pax = 3;
      else pax = dayPaxCount;
      break;

    case "transfer":
      pax = dayPaxCount;
      break;

    default:
      pax = 1;
      if (!resourceExist) qtyToAdd = dayPaxCount;
      break;
  }

  const newResources = [];
  for (let i = 0; i < qtyToAdd; i++) {
    resourceKey.value += 1;
    newResources.push({
      id: resourceKey.value,
      pax,
      nights: 1,
      resource: {
        id: resource.id,
        type: resource.type,
      },
    });
  }

  if (resourceExist) {
    resourceExist.tourDayItemResources.push(...newResources);
    resourceExist.quantity += qtyToAdd;
  } else {
    resourcesSelected.value.push({
      data: resource,
      quantity: qtyToAdd,
      pax,
      tourDayItemResources: newResources,
      paxes: currentTourDayItem.value?.paxes
    });
  }

  // Default to grouped view wherever grouping is applicable (more than one entry).
  const finalCount = resourceExist
    ? resourceExist.tourDayItemResources.length
    : qtyToAdd;
  groupByMode[resource.id] = finalCount > 1;
};

// Remove a resource
const removeResource = (resource: any, IsRemoveAllResource = false) => {
  const resourceExist = checkExistResource(resource);

  if (resourceExist) {
    if (resourceExist.quantity <= 1 || IsRemoveAllResource) {
      resourcesSelected.value = resourcesSelected.value.filter((el) => el.data.id !== resource.id);
      groupByMode[resource.id] = false;
    } else {
      resourceExist.quantity -= 1;

      if (resourceExist.tourDayItemResources.length > 0) {
        resourceExist.tourDayItemResources.pop();
      }

      // Grouping is only applicable when more than one entry remains.
      groupByMode[resource.id] = resourceExist.tourDayItemResources.length > 1;
    }
  }

  if (resourcesSelected.value.length === 0) {
    resourcesNextStep.value = false;
  }
};

// Show resources dates
const showResourcesDates = () => {
  resourcesMapped.value = [];
  resourcesSelected.value.forEach((resource) => {
    for (let i = 0; i < resource.quantity; i++) {
      resourcesMapped.value.push({ ...resource.data });
    }
  });
  resourcesNextStep.value = true;
};

// Remove resource from all
const removeResourceFromAll = (resource: any, removeResourceFn: (r: any) => void) => {
  removeResourceFn(resource); // Pass your removeResource arrow function here
};

// Calculate total pax for a resource
const resourceItemTotalResourcePax = (resourceId: number) => {
  const resource = resourcesSelected.value.find((r) => r.data.id === resourceId);

  if (!resource || resource.tourDayItemResources.length === 0) return 0;

  return resource.tourDayItemResources.reduce((total: number, r: any) => {
    const resourceType = r?.resource?.type || r?.supplier_resource?.type;
    if (resourceType === "transfer" || resourceType === "hotel") {
      const paxCount = r?.pax ?? r?.paxes?.length ?? 0;
      return total + (Number(paxCount) || 0);
    } else {
      return total + 1;
    }
  }, 0);
};

const removeTourDayItemResource = (resourceId: number, tourDayItemResource: any, IsRemoveAllResource = false) => {
  const resourceExist = resourcesSelected.value.find((r) => r.data.id === resourceId);

  if (!resourceExist) return;

  if (resourceExist.quantity <= 1 || IsRemoveAllResource) {
    // Remove the entire resource
    resourcesSelected.value = resourcesSelected.value.filter((r) => r.data.id !== resourceId);
  } else {
    // Decrease quantity and remove the specific tourDayItemResource
    resourceExist.quantity -= 1;
    resourceExist.tourDayItemResources = resourceExist.tourDayItemResources.filter(
      (r: { id: any }) => r.id !== tourDayItemResource.id
    );
  }
};
</script>

<style lang="scss" scoped>
.accordion-item {
  background-color: transparent;
}

.accordion .accordion-item .accordion-button {
  font-size: 1rem;
}

.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
}

.accordion .accordion-item .accordion-button:before,
.accordion .accordion-item .accordion-button:after {
  display: none;
}

.item-form-separator {
  border: 0;
  border-top: 1px solid #000;
  margin: 0;
  opacity: 1;
}
</style>
